import api from "../api";
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  Clock3,
  AlertCircle,
  CheckCircle2,
  Euro,
  User,
  Building2,
  Trash2,
  Pencil,
} from "lucide-react";

const TIPOLOGIE = [
  "Da quotare",
  "Da valutare",
  "Da richiamare",
  "Promemoria",
];

const PRIORITA = ["Alta", "Media", "Bassa"];
const STATI = ["Da fare", "In attesa", "Completato"];

const STORAGE_KEY = "ethos_opportunita_promemoria";

function loadItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function emptyForm() {
  return {
    titolo: "",
    cliente: "",
    tipologia: "Da quotare",
    descrizione: "",
    scadenza: "",
    priorita: "Media",
    responsabile: "",
    importo: "",
    fornitore_id: "",
    fornitore_nome: "",
    importo_fornitore: "",
    stato: "Da fare",
  };
}

export default function ProgrammaLavori() {
  const [items, setItems] = useState([]);
  const [fornitori, setFornitori] = useState([]);

  useEffect(() => {
    api.get("/entita/fornitori/stats")
      .then((response) => setFornitori(Array.isArray(response.data) ? response.data : []))
      .catch((error) => console.error("Errore caricamento fornitori:", error));
  }, []);
  useEffect(() => {
    api.get("/opportunita-promemoria")
      .then((response) => setItems(response.data))
      .catch((error) => console.error("Errore caricamento opportunita:", error));
  }, []);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [invioFatturazione, setInvioFatturazione] = useState(false);

  async function richiediFatturazione() {
    if (!editingId || invioFatturazione) return;
    const originale = items.find((item) => item.id === editingId);
    if (!originale) return window.alert("Opportunita non trovata.");

    const campi = ["titolo", "cliente", "tipologia", "descrizione", "scadenza", "priorita", "responsabile", "importo", "fornitore_id", "fornitore_nome", "importo_fornitore", "stato"];
    if (campi.some((campo) => String(form[campo] ?? "") !== String(originale[campo] ?? ""))) {
      return window.alert("Salva prima le modifiche, poi riapri l'opportunita per richiedere la fatturazione.");
    }

    if (!window.confirm("Inviare questa opportunita a Contabilita come richiesta di fatturazione?")) return;

    setInvioFatturazione(true);
    try {
      await api.post(`/opportunita-promemoria/${editingId}/richiedi-fatturazione`);
      window.alert("Richiesta inviata a Contabilita - Richieste fatturazione.");
    } catch (error) {
      window.alert(error.response?.data?.detail || "Invio richiesta non riuscito.");
    } finally {
      setInvioFatturazione(false);
    }
  }

  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("Tutti");

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchSearch =
        !search ||
        item.titolo?.toLowerCase().includes(search.toLowerCase()) ||
        item.cliente?.toLowerCase().includes(search.toLowerCase()) ||
        item.descrizione?.toLowerCase().includes(search.toLowerCase());

      const matchFiltro =
        filtro === "Tutti" ||
        item.tipologia === filtro ||
        item.stato === filtro;

      return matchSearch && matchFiltro;
    });
  }, [items, search, filtro]);

  const aperti = items.filter((i) => i.stato !== "Completato").length;
  const completati = items.filter((i) => i.stato === "Completato").length;
  const scaduti = items.filter((i) => {
    if (!i.scadenza || i.stato === "Completato") return false;
    return new Date(i.scadenza) < new Date(new Date().toDateString());
  }).length;

  function persist(next) {
    setItems(next);
    saveItems(next);
  }

  async function submit(e) {
    e.preventDefault();

    if (!form.titolo.trim()) return;

    try {
      if (editingId) {
        await api.put(`/opportunita-promemoria/${editingId}`, form);
      } else {
        await api.post("/opportunita-promemoria", form);
      }
      const response = await api.get("/opportunita-promemoria");
      setItems(response.data);
    } catch (error) {
      console.error("Errore salvataggio:", error);
      window.alert("Salvataggio non riuscito. Riprova.");
      return;
    }

    setForm(emptyForm());
    setEditingId(null);
    setShowForm(false);
  }

  function editItem(item) {
    setForm({
      titolo: item.titolo || "",
      cliente: item.cliente || "",
      tipologia: item.tipologia || "Da quotare",
      descrizione: item.descrizione || "",
      scadenza: item.scadenza || "",
      priorita: item.priorita || "Media",
      responsabile: item.responsabile || "",
      importo: item.importo ?? "",
      fornitore_id: item.fornitore_id || "",
      fornitore_nome: item.fornitore_nome || "",
      importo_fornitore: item.importo_fornitore ?? "",
      stato: item.stato || "Da fare",
    });
    setEditingId(item.id);
    setShowForm(true);
  }

  async function deleteItem(id) {
    if (!window.confirm("Eliminare questa voce?")) return;
    try {
      await api.delete(`/opportunita-promemoria/${id}`);
      const response = await api.get("/opportunita-promemoria");
      setItems(response.data);
    } catch (error) {
      console.error("Errore eliminazione:", error);
      window.alert("Eliminazione non riuscita. Riprova.");
    }
  }

  async function importaDatiLocali() {
    const vecchieVoci = loadItems();
    if (!vecchieVoci.length) return window.alert("Nessun dato da importare in questo browser.");
    if (!window.confirm("Importare " + vecchieVoci.length + " voci nel database condiviso?")) return;
    try {
      const result = await api.post("/opportunita-promemoria/import", vecchieVoci);
      const response = await api.get("/opportunita-promemoria");
      setItems(response.data);
      window.alert("Importazione completata: " + result.data.inserite + " nuove voci.");
    } catch (error) {
      console.error("Errore importazione:", error);
      window.alert("Importazione non riuscita. I dati originali restano nel browser.");
    }
  }

  function badgePriorita(p) {
    if (p === "Alta") return "bg-rose-100 text-rose-700";
    if (p === "Media") return "bg-amber-100 text-amber-700";
    return "bg-emerald-100 text-emerald-700";
  }

  return (
    <div className="space-y-6" data-testid="page-opportunita-promemoria">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Gestione commerciale</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">
            Opportunità e Promemoria
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Progetti da quotare, attività da valutare, richiami e cose da ricordare.
          </p>
        </div>

        <button
          onClick={() => {
            setForm(emptyForm());
            setEditingId(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 font-semibold"
        >
          <Plus className="w-4 h-4" />
          Nuovo
        </button>
        <button type="button" onClick={importaDatiLocali} className="border border-slate-300 px-4 py-2 font-semibold">Importa dati da questo browser</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Aperti" value={aperti} icon={Clock3} />
        <StatCard label="Scaduti" value={scaduti} icon={AlertCircle} />
        <StatCard label="Completati" value={completati} icon={CheckCircle2} />
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca titolo, cliente o note..."
            className="w-full border border-slate-300 pl-9 pr-3 py-2 bg-white"
          />
        </div>

        <select
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className="border border-slate-300 px-3 py-2 bg-white"
        >
          <option>Tutti</option>
          {TIPOLOGIE.map((x) => <option key={x}>{x}</option>)}
          {STATI.map((x) => <option key={x}>{x}</option>)}
        </select>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          className="bg-white border border-slate-300 p-5 space-y-4 shadow-sm"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Titolo">
              <input
                value={form.titolo}
                onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
                required
              />
            </Field>

            <Field label="Cliente / Contatto">
              <input
                value={form.cliente}
                onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>

            <Field label="Tipologia">
              <select
                value={form.tipologia}
                onChange={(e) => setForm({ ...form, tipologia: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              >
                {TIPOLOGIE.map((x) => <option key={x}>{x}</option>)}
              </select>
            </Field>

            <Field label="Stato">
              <select
                value={form.stato}
                onChange={(e) => setForm({ ...form, stato: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              >
                {STATI.map((x) => <option key={x}>{x}</option>)}
              </select>
            </Field>

            <Field label="Scadenza / Promemoria">
              <input
                type="date"
                value={form.scadenza}
                onChange={(e) => setForm({ ...form, scadenza: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>

            <Field label="Priorità">
              <select
                value={form.priorita}
                onChange={(e) => setForm({ ...form, priorita: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              >
                {PRIORITA.map((x) => <option key={x}>{x}</option>)}
              </select>
            </Field>

            <Field label="Responsabile">
              <input
                value={form.responsabile}
                onChange={(e) => setForm({ ...form, responsabile: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>

            <Field label="Fornitore / Subappaltatore">
              <select
                value={form.fornitore_id}
                onChange={(e) => {
                  const selected = fornitori.find((f) => f.fornitore_id === e.target.value);
                  setForm({
                    ...form,
                    fornitore_id: e.target.value,
                    fornitore_nome: selected?.ragione_sociale || "",
                  });
                }}
                className="w-full border border-slate-300 px-3 py-2 bg-white"
              >
                <option value="">Nessun fornitore selezionato</option>
                {fornitori.map((f) => (
                  <option key={f.fornitore_id} value={f.fornitore_id}>
                    {f.ragione_sociale}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Importo da fatturare (€)">
              <input
                type="number"
                step="0.01"
                value={form.importo}
                onChange={(e) => setForm({ ...form, importo: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>

            <Field label="Importo fornitore (€)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.importo_fornitore}
                onChange={(e) => setForm({ ...form, importo_fornitore: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>
          </div>

          <Field label="Descrizione / Note">
            <textarea
              rows={4}
              value={form.descrizione}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2"
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setForm(emptyForm());
              }}
              className="px-4 py-2 border border-slate-300"
            >
              Annulla
            </button>
            {editingId && (
              <button
                type="button"
                onClick={richiediFatturazione}
                disabled={invioFatturazione}
                className="px-4 py-2 bg-emerald-700 text-white font-semibold disabled:opacity-50"
              >
                {invioFatturazione ? "Invio in corso..." : "Richiesta fatturazione"}
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-slate-900 text-white font-semibold"
            >
              {editingId ? "Salva modifiche" : "Crea voce"}
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-3">
        {filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-10 text-center text-slate-500">
            Nessuna opportunità o promemoria presente.
          </div>
        ) : (
          filtered.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-slate-300 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg">{item.titolo}</h3>
                    <span className={`text-xs px-2 py-1 ${badgePriorita(item.priorita)}`}>
                      {item.priorita}
                    </span>
                    <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700">
                      {item.tipologia}
                    </span>
                    <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700">
                      {item.stato}
                    </span>
                  </div>

                  {item.cliente && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Building2 className="w-4 h-4" />
                      {item.cliente}
                    </div>
                  )}

                  {item.responsabile && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <User className="w-4 h-4" />
                      {item.responsabile}
                    </div>
                  )}

                  {item.scadenza && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock3 className="w-4 h-4" />
                      {new Date(item.scadenza).toLocaleDateString("it-IT")}
                    </div>
                  )}

                  {item.importo && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Euro className="w-4 h-4" />
                      {Number(item.importo).toLocaleString("it-IT", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  )}

                  {item.descrizione && (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {item.descrizione}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => editItem(item)}
                    className="p-2 border border-slate-300"
                    title="Modifica"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="p-2 border border-rose-300 text-rose-700"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-300 p-4 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div className="text-2xl font-black">{value}</div>
      </div>
      <Icon className="w-5 h-5 text-slate-500" />
    </div>
  );
}
